/**
 * Debug State Collector
 * Collects all relevant info for support troubleshooting
 */

import { useStore } from '@store/useStore'
import { getStats, getStatsSummary } from '@services/tokenStats'
import { getSession } from '@services/auth'
import { getCredits } from '@services/stripe'

/**
 * Get system information
 */
function getSystemInfo() {
  const ua = navigator.userAgent
  let os = 'Unknown'
  let browser = 'Unknown'
  let browserVersion = ''

  // Detect OS
  if (ua.includes('Win')) os = 'Windows'
  else if (ua.includes('Mac')) os = 'MacOS'
  else if (ua.includes('Linux')) os = 'Linux'
  else if (ua.includes('Android')) os = 'Android'
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'

  // Detect browser
  if (ua.includes('Firefox/')) {
    browser = 'Firefox'
    browserVersion = ua.match(/Firefox\/(\d+)/)?.[1] || ''
  } else if (ua.includes('Edg/')) {
    browser = 'Edge'
    browserVersion = ua.match(/Edg\/(\d+)/)?.[1] || ''
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome'
    browserVersion = ua.match(/Chrome\/(\d+)/)?.[1] || ''
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari'
    browserVersion = ua.match(/Version\/(\d+)/)?.[1] || ''
  }

  return {
    os,
    browser: `${browser} ${browserVersion}`.trim(),
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    userAgent: ua,
    online: navigator.onLine,
  }
}

/**
 * Get app configuration from localStorage
 */
function getAppConfig() {
  return {
    provider: localStorage.getItem('emd_provider') || 'claude',
    model: localStorage.getItem('emd_model') || 'sonnet',
    useDualModel: localStorage.getItem('emd_use_dual_model') === 'true',
    reasoningModel: localStorage.getItem('emd_reasoning_model') || 'sonnet',
    productionModel: localStorage.getItem('emd_production_model') || 'opus',
    cmsPattern: localStorage.getItem('emd_cms_pattern') || 'tailwind',
    language: localStorage.getItem('emd_language') || 'fr',
    antiFootprint: localStorage.getItem('emd_anti_footprint') !== 'false',
    debugMode: localStorage.getItem('emd_debug_mode') === 'true',
  }
}

/**
 * Get pipeline state from store
 */
function getPipelineState() {
  const state = useStore.getState()
  const { project, files, images, linkPlacements, progress, summaries } = state

  // Count pages by status
  const fileList = Object.keys(files)
  const htmlFiles = fileList.filter(f => f.endsWith('.html'))

  // Get architecte expected pages
  const arch = project.context?.architecte
  const expectedPages = arch?.pages || arch?.architecture?.pages || []

  return {
    domain: project.domain || null,
    currentPhase: project.phase || 'init',
    pipelineStatus: progress.status,
    pipelineMessage: progress.message,
    pagesGenerated: htmlFiles.length,
    pagesExpected: expectedPages.length,
    missingPages: expectedPages
      .filter(p => !fileList.includes(p.file))
      .map(p => p.file)
      .slice(0, 10), // Limit to 10
    imagesGenerated: images?.length || 0,
    linkPlacements: linkPlacements ? {
      moneySite: linkPlacements.moneySite?.page || null,
      resourceLinksCount: linkPlacements.resourceLinks?.length || 0,
    } : null,
    completedPhases: Object.keys(summaries || {}),
  }
}

/**
 * Get token/cost statistics
 */
function getTokenStats() {
  const stats = getStats()
  const summary = getStatsSummary()

  return {
    totalCost: summary.totalCost,
    totalTokens: summary.totalTokens,
    inputTokens: summary.inputTokens,
    outputTokens: summary.outputTokens,
    llmCalls: summary.calls,
    duration: `${summary.duration}s`,
    byPhase: summary.costPerPhase,
    byModel: summary.costPerModel,
  }
}

/**
 * Get account info
 */
function getAccountInfo() {
  const session = getSession()
  const credits = getCredits()

  return {
    email: session?.email || 'non connecté',
    credits: credits,
    hasCustomerId: !!session?.customerId,
  }
}

/**
 * Get last error if any (from store)
 */
function getLastError() {
  const state = useStore.getState()
  return state.lastError || null
}

/**
 * Collect all debug state
 */
export function collectDebugState() {
  const timestamp = new Date().toISOString()

  return {
    timestamp,
    version: '1.0.0', // App version - could be from package.json
    system: getSystemInfo(),
    config: getAppConfig(),
    pipeline: getPipelineState(),
    tokens: getTokenStats(),
    account: getAccountInfo(),
    lastError: getLastError(),
  }
}

/**
 * Format debug state as readable markdown text
 */
export function formatDebugState(state) {
  const lines = []

  lines.push('## EMD Factory - Debug State')
  lines.push(`Generated: ${new Date(state.timestamp).toLocaleString()} (${state.system.timezone})`)
  lines.push('')

  // System
  lines.push('### System')
  lines.push(`- OS: ${state.system.os}`)
  lines.push(`- Browser: ${state.system.browser}`)
  lines.push(`- Screen: ${state.system.screen}`)
  lines.push(`- Viewport: ${state.system.viewport}`)
  lines.push(`- Language: ${state.system.language}`)
  lines.push(`- Online: ${state.system.online ? 'Yes' : 'No'}`)
  lines.push('')

  // Configuration
  lines.push('### Configuration')
  lines.push(`- Provider: ${state.config.provider}`)
  lines.push(`- Model: ${state.config.model}`)
  lines.push(`- Dual-model: ${state.config.useDualModel ? `Yes (${state.config.reasoningModel} → ${state.config.productionModel})` : 'No'}`)
  lines.push(`- CMS: ${state.config.cmsPattern}`)
  lines.push(`- Language: ${state.config.language}`)
  lines.push(`- Anti-footprint: ${state.config.antiFootprint ? 'Yes' : 'No'}`)
  lines.push('')

  // Pipeline
  lines.push('### Pipeline')
  lines.push(`- Domain: ${state.pipeline.domain || 'None'}`)
  lines.push(`- Status: ${state.pipeline.pipelineStatus}`)
  lines.push(`- Current phase: ${state.pipeline.currentPhase}`)
  lines.push(`- Completed: ${state.pipeline.completedPhases.join(', ') || 'None'}`)
  lines.push('')

  // Results
  lines.push('### Results')
  lines.push(`- Pages: ${state.pipeline.pagesGenerated}/${state.pipeline.pagesExpected}`)
  if (state.pipeline.missingPages.length > 0) {
    lines.push(`- Missing: ${state.pipeline.missingPages.join(', ')}`)
  }
  lines.push(`- Images: ${state.pipeline.imagesGenerated}`)
  if (state.pipeline.linkPlacements) {
    lines.push(`- Money site: ${state.pipeline.linkPlacements.moneySite || 'None'}`)
    lines.push(`- Resource links: ${state.pipeline.linkPlacements.resourceLinksCount}`)
  }
  lines.push('')

  // Tokens
  lines.push('### Token Usage')
  lines.push(`- Total cost: ${state.tokens.totalCost}`)
  lines.push(`- Total tokens: ${state.tokens.totalTokens.toLocaleString()} (${state.tokens.inputTokens.toLocaleString()}→${state.tokens.outputTokens.toLocaleString()})`)
  lines.push(`- LLM calls: ${state.tokens.llmCalls}`)
  lines.push(`- Duration: ${state.tokens.duration}`)
  if (state.tokens.byPhase.length > 0) {
    lines.push('- By phase:')
    state.tokens.byPhase.forEach(p => {
      lines.push(`  • ${p.phase}: ${p.cost} (${p.tokens.toLocaleString()} tokens)`)
    })
  }
  lines.push('')

  // Account
  lines.push('### Account')
  lines.push(`- Email: ${state.account.email}`)
  lines.push(`- Credits: ${state.account.credits}`)
  lines.push('')

  // Error
  lines.push('### Last Error')
  if (state.lastError) {
    lines.push(`- Message: ${state.lastError.message}`)
    lines.push(`- Phase: ${state.lastError.phase || 'N/A'}`)
    lines.push(`- Time: ${state.lastError.timestamp || 'N/A'}`)
  } else {
    lines.push('- None')
  }

  return lines.join('\n')
}

/**
 * Copy debug state to clipboard
 */
export async function copyDebugState() {
  const state = collectDebugState()
  const formatted = formatDebugState(state)

  try {
    await navigator.clipboard.writeText(formatted)
    return { success: true, state }
  } catch (err) {
    console.error('Failed to copy debug state:', err)
    return { success: false, error: err.message }
  }
}

export default {
  collectDebugState,
  formatDebugState,
  copyDebugState,
}
